import firebase from "firebase/app";
import { fetchFirestoreCollectionBySnapshotMap } from "utility/firebase";

import { PAYMENT_STATUS } from "../../constants/paymentStatus";
import { EventDocumentSchema } from "../../schema/eventImageSchema";
import { SettlementSchema } from "../../schema/settlementSchema";

import type { EventImage } from "../../types/eventImage";
import type { ParsedSettlement } from "../../types/parsedSettlement";
import type { ParsedSettlementBase } from "../../types/parsedSettlementBase";

const isSuccessStatus = (settlement: ParsedSettlementBase): boolean => {
  if (settlement.status === null || settlement.status === undefined) {
    return false;
  }

  if (settlement.status === PAYMENT_STATUS.PAYSUCCESS) {
    return true;
  }

  // 未払いステータスの場合、キャンセルしていないもののみ表示
  // お支払い期限を過ぎても、settlement.statusが「CANCEL」にならないケースがあるため、 isCancelledも確認する
  if (settlement.status === PAYMENT_STATUS.UNPROCESS) {
    return settlement.isCancelled === false;
  }

  return false;
};

// 過去に移行処理でスタッフ分を移行していなかったため、productsフィールドがないものを除外。
// https://github.com/balus-co-ltd/spwn/issues/323
// TODO: 上記解決しているか確認。解決していたらフィルター処理は削除する。
const hasItem = (settlement: ParsedSettlementBase): boolean => {
  if (Array.isArray(settlement.lineItems)) {
    return settlement.lineItems.length > 0;
  }
  if (!Array.isArray(settlement.products)) {
    return false;
  }
  return settlement.products.length > 0;
};

type FetchEventImage = (eventId: string) => Promise<EventImage>;

const fetchEventImage: FetchEventImage = async (eventId) => {
  try {
    const eventDocument = await firebase
      .firestore()
      .collection("events")
      .doc(eventId)
      .get();

    if (!eventDocument.exists) {
      return { bannerImage: "", defaultImage: "" };
    }

    const parseResult = EventDocumentSchema.safeParse(eventDocument.data());
    if (!parseResult.success) {
      console.error("Failed to parse event document:", parseResult.error);
      return { bannerImage: "", defaultImage: "" };
    }

    const eventData = parseResult.data;
    return {
      bannerImage: eventData.storagePath?.eventBanner ?? "",
      defaultImage: eventData.defaultImg ?? "",
    };
  } catch (error) {
    console.error("Failed to fetch event document:", error);
    return { bannerImage: "", defaultImage: "" };
  }
};

type FetchAllEventImagesFromProducts = (
  products:
    | Array<{
        eid?: string;
        productType?: string;
      }>
    | null
    | undefined
) => Promise<Record<string, EventImage>>;

const fetchAllEventImagesFromProducts: FetchAllEventImagesFromProducts = async (
  products
) => {
  if (!Array.isArray(products) || products.length === 0) {
    return {};
  }

  const uniqueEventIds = Array.from(
    new Set(
      products
        .map((product) => product.eid)
        .filter((eid): eid is string => eid !== undefined && eid !== null)
    )
  );

  if (uniqueEventIds.length === 0) {
    return {};
  }

  const imageMap: Record<string, EventImage> = {};

  await Promise.all(
    uniqueEventIds.map(async (eventId) => {
      imageMap[eventId] = await fetchEventImage(eventId);
    })
  );

  return imageMap;
};

export const fetchSettlements = async (
  uid: string
): Promise<ParsedSettlement[]> => {
  if (uid === "") {
    return [];
  }

  const ref = `settlement/${uid}/activeTransactions`;
  const snapshot = firebase
    .firestore()
    .collection(ref)
    .where("isPurchased", "==", true)
    .get();

  const data: unknown = await fetchFirestoreCollectionBySnapshotMap(snapshot);

  if (data === null || data === undefined) {
    return [];
  }

  const validSettlements = Object.values(data).reduce<ParsedSettlementBase[]>(
    (previousValue, currentValue) => {
      const parsedItem = SettlementSchema.safeParse(currentValue);
      if (!parsedItem.success) return previousValue;

      const settlement = parsedItem.data;

      if (!isSuccessStatus(settlement)) return previousValue;

      if (!hasItem(settlement)) return previousValue;

      previousValue.push(settlement);
      return previousValue;
    },
    []
  );

  const allSettlements = await Promise.all(
    validSettlements.map(async (settlement): Promise<ParsedSettlement> => {
      const eventImages = await fetchAllEventImagesFromProducts(
        settlement.products
      );

      return {
        ...settlement,
        eventImages,
      };
    })
  );

  return allSettlements.sort(
    ({ orderId: firstOrderId }, { orderId: secondOrderId }) => {
      return Number(secondOrderId) - Number(firstOrderId);
    }
  );
};
